import { PostalAddressCard } from "@/services/backend/addresses/components/postal-address-card";
import { ServiceAddressCard } from "@/services/backend/addresses/components/service-address-card";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Address,
  newAddress,
} from "@/services/backend/addresses/types/address";
import { useMemo } from "react";
import { useQpmBuildingServiceAddressGetQuery } from "@/services/backend/qpm/buildings/api/building-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";

export type ManageBuildingAddressesSectionProps = {
  postalAddress: Address;
  serviceObjectIds: string[];
  selectedIndex: number;
  onPrev: () => void;
  onNext: () => void;
};

export function ManageBuildingAddressesSection({
  postalAddress,
  serviceObjectIds,
  selectedIndex,
  onPrev,
  onNext,
}: ManageBuildingAddressesSectionProps) {
  const selectedServiceObjectId = useMemo(() => {
    if (!serviceObjectIds.length) return undefined;
    return serviceObjectIds[selectedIndex];
  }, [serviceObjectIds, selectedIndex]);

  const {
    data: serviceAddressResponse,
    error: serviceAddressError,
    isLoading: isLoadingServiceAddress,
  } = useQpmBuildingServiceAddressGetQuery(
    { serviceObjectId: selectedServiceObjectId ?? "" },
    {
      skip: !selectedServiceObjectId,
    },
  );

  const selectedServiceAddress =
    serviceAddressResponse?.address ?? newAddress();

  return (
    <div className="space-y-4">
      <PostalAddressCard
        address={postalAddress}
        onAddressChange={() => {
          // Read-only for now; no mutations for AddressV2.
        }}
        title={t("Postadresse")}
        description={t(
          "Adresse des Kunden, wie sie für die Gebäude hinterlegt ist.",
        )}
        disabled
      />

      {serviceObjectIds.length === 0 && (
        <div className="text-muted-foreground text-sm">
          {t("Keine Serviceadressen vorhanden")}
        </div>
      )}

      {serviceObjectIds.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Button variant="outline" size="icon" onClick={onPrev}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-muted-foreground text-sm">
              {t("Serviceadresse")} {selectedIndex + 1} {t("von")}{" "}
              {serviceObjectIds.length}
            </div>
            <Button variant="outline" size="icon" onClick={onNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {serviceAddressError && (
            <RTKQueryErrorAlert error={serviceAddressError} />
          )}

          <ServiceAddressCard
            address={selectedServiceAddress}
            onAddressChange={() => {
              // Read-only for now; no mutations for AddressV2.
            }}
            hideNavigationButton={false}
            disabled
          />

          {selectedServiceObjectId && (
            <div className="flex justify-end">
              <a
                className="text-sm underline"
                href={`/qpm/service-objects/${selectedServiceObjectId}`}
              >
                {t("Zum Serviceobjekt")}
              </a>
            </div>
          )}

          {isLoadingServiceAddress && (
            <div className="text-muted-foreground text-sm">
              {t("Lade Serviceadresse...")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
